import React, { createContext, useContext, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MenuNode } from "@/routes";
import { Tab, TabsContextValue, TabsState } from "./TabsContext.types";

const MAX_TABS = 8;
const STORAGE_KEY = "ged:tabs";

const TabsContext = createContext<TabsContextValue | null>(null);

function generateId(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// Duas abas do MESMO item viram "Título" e "Título (2)" — compara path E label, comparando só pelo
// rótulo BASE (ignorando um sufixo "(N)" já existente), não por igualdade exata: senão a 3ª aba do
// mesmo item colidia com a 2ª, as duas viravam "Título (2)". Path sozinho não basta:
// Incluir/Consultar/Alterar/Excluir do menu dinâmico compartilham o path (mesma tela), mas são itens
// diferentes — não devem contar como duplicata um do outro (ver menuTree.buildMenuTree).
function computeLabel(tabs: Tab[], node: MenuNode): string {
  const pattern = new RegExp(`^${escapeRegExp(node.label)}(?: \\((\\d+)\\))?$`);
  const occurrences = tabs
    .filter((t) => t.path === node.path)
    .map((t) => t.label.match(pattern))
    .filter((m): m is RegExpMatchArray => m !== null)
    .map((m) => (m[1] ? Number(m[1]) : 1));
  return occurrences.length === 0 ? node.label : `${node.label} (${Math.max(...occurrences) + 1})`;
}

// sessionStorage, não localStorage — mesma vida útil da sessão Curio (também em sessionStorage, ver
// SessionManager/AuthProvider). Com localStorage, fechar o navegador e abrir de novo (sem logout
// explícito) reabria as abas de uma sessão já encerrada. logout() (useAuthQuery.ts) já limpa
// sessionStorage por inteiro no logout — não é necessário um clearTabsStorage() separado.
function persist(state: TabsState): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* sessionStorage indisponível — abas só não sobrevivem ao refresh */
  }
}

function loadFromStorage(): TabsState {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as TabsState;
  } catch {
    /* ignore corrupt data */
  }
  return { tabs: [], activeTabId: null };
}

export const TabsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const initial = loadFromStorage();
  const [tabs, setTabs] = useState<Tab[]>(initial.tabs);
  const [activeTabId, setActiveTabId] = useState<string | null>(initial.activeTabId);
  const [warningOpen, setWarningOpen] = useState(false);
  const closeCallbacks = useRef<Map<string, () => void>>(new Map());

  const openTab = (node: MenuNode) => {
    if (!node.path) return;
    if (tabs.length >= MAX_TABS) {
      setWarningOpen(true);
      return;
    }
    const label = computeLabel(tabs, node);
    const newTab: Tab = { id: generateId(), path: node.path, label, initialAction: node.initialAction };
    const newTabs = [...tabs, newTab];
    setTabs(newTabs);
    setActiveTabId(newTab.id);
    persist({ tabs: newTabs, activeTabId: newTab.id });
  };

  const closeTab = (id: string) => {
    closeCallbacks.current.get(id)?.();
    closeCallbacks.current.delete(id);

    const idx = tabs.findIndex((t) => t.id === id);
    const newTabs = tabs.filter((t) => t.id !== id);

    let newActiveId: string | null = activeTabId;
    if (activeTabId === id) {
      if (newTabs.length === 0) {
        newActiveId = null;
        navigate("/inicio");
      } else {
        newActiveId = newTabs[Math.max(0, idx - 1)].id;
      }
    }

    setTabs(newTabs);
    setActiveTabId(newActiveId);
    persist({ tabs: newTabs, activeTabId: newActiveId });
  };

  const registerCloseCallback = (id: string, fn: () => void) => {
    closeCallbacks.current.set(id, fn);
  };

  const unregisterCloseCallback = (id: string) => {
    closeCallbacks.current.delete(id);
  };

  const setActiveTab = (id: string | null) => {
    setActiveTabId(id);
    persist({ tabs, activeTabId: id });
  };

  const dismissWarning = () => setWarningOpen(false);

  return (
    <TabsContext.Provider
      value={{
        tabs,
        activeTabId,
        openTab,
        closeTab,
        setActiveTab,
        warningOpen,
        dismissWarning,
        registerCloseCallback,
        unregisterCloseCallback
      }}>
      {children}
    </TabsContext.Provider>
  );
};

export const useTabs = (): TabsContextValue => {
  const ctx = useContext(TabsContext);
  if (!ctx) throw new Error("useTabs must be used within TabsProvider");
  return ctx;
};
